// =============================================================================
// VandieZ admin-web -- Audit log (/audit-log)
// =============================================================================
// Single timeline view across audit.kyc_review_changes,
// audit.user_role_changes, audit.user_status_changes. The
// `action_kind` discriminator drives the per-row icon + label.
//
// Filters land in the URL so an operator can share a link to "every
// suspension actioned by Rajesh last week".
// =============================================================================

import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  ClipboardList,
  Filter,
  ShieldCheck,
  UserCog,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { auditAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { UserPicker } from "@/components/UserPicker";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TableSkeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { AdminUserSummary } from "@/types/rbac";
import type { AuditActionKind, AuditLogEntry } from "@/types/audit";

const PAGE_SIZE = 100;

const KIND_FILTERS: { label: string; value: AuditActionKind | "all" }[] = [
  { label: "All", value: "all" },
  { label: "KYC", value: "kyc" },
  { label: "Roles", value: "role" },
  { label: "Status", value: "status" },
];

function actionKindMeta(kind: AuditActionKind): {
  icon: React.JSX.Element;
  label: string;
  classes: string;
} {
  switch (kind) {
    case "kyc":
      return {
        icon: <CircleCheck className="h-3.5 w-3.5" />,
        label: "KYC",
        classes: "bg-success-subtle text-success-subtle-foreground",
      };
    case "role":
      return {
        icon: <ShieldCheck className="h-3.5 w-3.5" />,
        label: "Role",
        classes: "bg-primary-subtle text-primary-subtle-foreground",
      };
    case "status":
      return {
        icon: <UserCog className="h-3.5 w-3.5" />,
        label: "Status",
        classes: "bg-warning-subtle text-warning-subtle-foreground",
      };
  }
}

function formatChange(row: AuditLogEntry): string {
  if (row.action_kind === "role") {
    return `${row.new_value} ${row.related_label ?? row.related_id ?? ""}`.trim();
  }
  if (row.old_value !== null && row.old_value !== row.new_value) {
    return `${row.old_value} → ${row.new_value}`;
  }
  return row.new_value;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function AuditLogPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actor, setActor] = useState<AdminUserSummary | null>(null);
  const [target, setTarget] = useState<AdminUserSummary | null>(null);

  const kindParam = (searchParams.get("kind") ?? "all") as
    | AuditActionKind
    | "all";
  const offset = Number.parseInt(searchParams.get("offset") ?? "0", 10) || 0;
  const since = searchParams.get("since") ?? "";
  const until = searchParams.get("until") ?? "";
  const actorId = searchParams.get("actor") ?? "";
  const targetId = searchParams.get("target") ?? "";

  const updateFilters = useCallback(
    (
      next: Partial<{
        kind: string;
        offset: number;
        since: string;
        until: string;
        actor: string;
        target: string;
      }>,
    ): void => {
      setSearchParams(
        (cur) => {
          const p = new URLSearchParams(cur);
          for (const [k, v] of Object.entries(next)) {
            if (
              v === undefined ||
              v === "" ||
              v === 0 ||
              (k === "kind" && v === "all")
            ) {
              p.delete(k);
            } else {
              p.set(k, String(v));
            }
          }
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const fetchPage = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {return;}
      setLoading(true);
      setError(null);
      try {
        const res = await auditAdminApi.search(
          accessToken,
          {
            limit: PAGE_SIZE,
            offset,
            ...(kindParam !== "all" ? { action_kind: kindParam } : {}),
            ...(actorId ? { actor_user_id: actorId } : {}),
            ...(targetId ? { target_user_id: targetId } : {}),
            ...(since ? { since } : {}),
            ...(until ? { until } : {}),
          },
          signal,
        );
        setItems(res.items);
        setTotal(res.total);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        const msg = e instanceof ApiError ? e.problem.detail : "Unexpected error.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, kindParam, offset, since, until, actorId, targetId],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchPage(c.signal);
    return () => { c.abort(); };
  }, [fetchPage]);

  const canPrev = offset > 0;
  const canNext = offset + items.length < total;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Audit log"
        description="Cross-table timeline of KYC decisions, role grants/revokes, and user status changes. Newest first."
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" /> Filters
          </CardTitle>
          <CardDescription>
            All filters compose. URL is the source of truth so links
            are shareable.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-1">
            {KIND_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => { updateFilters({ kind: f.value, offset: 0 }); }}
                className={cn(
                  "rounded px-3 py-1 text-sm",
                  kindParam === f.value
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Actor (the admin who did it)
              </label>
              <UserPicker
                value={actor}
                onChange={(u) => {
                  setActor(u);
                  updateFilters({ actor: u?.id ?? "", offset: 0 });
                }}
                roleFilter="admin"
                placeholder="Search admin by phone or name…"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Target (the user it happened to)
              </label>
              <UserPicker
                value={target}
                onChange={(u) => {
                  setTarget(u);
                  updateFilters({ target: u?.id ?? "", offset: 0 });
                }}
                placeholder="Search any user by phone or name…"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Since (inclusive)
              </label>
              <input
                type="datetime-local"
                value={since.slice(0, 16)}
                onChange={(e) => {
                  updateFilters({
                    since: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : "",
                    offset: 0,
                  });
                }}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Until (exclusive)
              </label>
              <input
                type="datetime-local"
                value={until.slice(0, 16)}
                onChange={(e) => {
                  updateFilters({
                    until: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : "",
                    offset: 0,
                  });
                }}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              />
            </div>
          </div>
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setActor(null);
                setTarget(null);
                setSearchParams({}, { replace: true });
              }}
            >
              <Filter className="mr-1 h-3.5 w-3.5" />
              Clear filters
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Timeline</CardTitle>
          <CardDescription>
            {total > 0
              ? `Showing ${offset + 1}–${offset + items.length} of ${total}.`
              : "No audit rows match these filters."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error !== null && (
            <Alert variant="destructive" className="mb-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading && items.length === 0 ? (
            <TableSkeleton rows={5} columns={6} />
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              Nothing happened in the chosen window. Loosen filters or
              widen the date range.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-4">When</th>
                    <th className="py-2 pr-4">Kind</th>
                    <th className="py-2 pr-4">Actor</th>
                    <th className="py-2 pr-4">Target</th>
                    <th className="py-2 pr-4">Change</th>
                    <th className="py-2 pr-4">Source / notes</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => {
                    const meta = actionKindMeta(row.action_kind);
                    return (
                      <tr key={row.id} className="border-b last:border-b-0 align-top">
                        <td className="py-3 pr-4 text-xs text-muted-foreground whitespace-nowrap">
                          {formatDate(row.changed_at)}
                        </td>
                        <td className="py-3 pr-4">
                          <span
                            className={cn(
                              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                              meta.classes,
                            )}
                          >
                            {meta.icon}
                            {meta.label}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          {row.actor_user_id !== null ? (
                            <Link
                              to={`/users/${row.actor_user_id}`}
                              className="hover:underline"
                            >
                              {row.actor_name ?? row.actor_user_id.slice(0, 8) + "…"}
                            </Link>
                          ) : (
                            <span className="text-muted-foreground">system</span>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          <Link
                            to={`/users/${row.target_user_id}`}
                            className="hover:underline"
                          >
                            {row.target_name ?? row.target_user_id.slice(0, 8) + "…"}
                          </Link>
                        </td>
                        <td className="py-3 pr-4 font-mono text-xs">
                          {formatChange(row)}
                        </td>
                        <td className="py-3 pr-4 text-xs text-muted-foreground max-w-md">
                          {row.change_source !== null && (
                            <div className="font-mono">{row.change_source}</div>
                          )}
                          {row.notes !== null && row.notes.length > 0 && (
                            <div className="mt-0.5 line-clamp-2">{row.notes}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={!canPrev || loading}
              onClick={() => { updateFilters({ offset: Math.max(0, offset - PAGE_SIZE) }); }}
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Previous
            </Button>
            <span className="text-xs text-muted-foreground">
              {loading ? "Loading…" : `Page ${Math.floor(offset / PAGE_SIZE) + 1}`}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!canNext || loading}
              onClick={() => { updateFilters({ offset: offset + PAGE_SIZE }); }}
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
